// «Cobro de hoy»: una caja por canal con lo enviado hoy contra el tope y la tira de 14 días.
import { useMemo } from 'react';
import { COLLECTION_QUOTA_DAYS } from '@/api/services/loan-credit';
import { Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatShortDayMx } from '@/lib/dateHelpers';
import { DetailCard } from '@/shared/patterns/DetailCard';
import { COPY, quotaViewsOf, type QuotaCell, type QuotaChannelView } from './customerMessages';
import { useCollectionQuota } from './useCustomerMessagesData';

const CELL_CLASS: Record<QuotaCell, string> = {
    sent: 'border-primary bg-primary',
    blocked: 'border-dashed border-warning bg-transparent',
    empty: 'border-border bg-muted/40',
};

const CELL_TITLE: Record<QuotaCell, string> = {
    sent: 'enviado',
    blocked: 'bloqueado por el tope',
    empty: 'sin mensajes',
};

function QuotaBox({ view }: { view: QuotaChannelView }) {
    return (
        <section
            aria-label={COPY.quotaChannelLabel(view.label)}
            className={cn(
                'min-w-0 rounded-md border px-2.5 py-2',
                view.used ? 'border-warning-soft-border bg-warning-soft' : 'border-border bg-card',
            )}
        >
            <div className="flex items-baseline justify-between gap-1">
                <h4 className="text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    {view.label}
                </h4>
                <span
                    className={cn(
                        'text-[10px] font-semibold',
                        view.used ? 'text-warning' : 'text-success',
                    )}
                >
                    {view.used ? COPY.quotaUsed : COPY.quotaAvailable}
                </span>
            </div>
            <p className="text-[15px] font-bold tabular-nums">
                {view.sentToday} / {view.max}
            </p>
            <ol className="mt-1 flex gap-[2px]" aria-label={COPY.quotaStripLabel(view.label)}>
                {view.strip.map(({ day, cell }) => (
                    <li
                        key={day}
                        title={`${formatShortDayMx(day)}: ${CELL_TITLE[cell]}`}
                        data-cell={cell}
                        className={cn(
                            'h-2.5 min-w-0 flex-1 rounded-[2px] border',
                            CELL_CLASS[cell],
                        )}
                    />
                ))}
            </ol>
        </section>
    );
}

export function CollectionQuotaPanel({ retailCustomerId }: { retailCustomerId: string }) {
    const quota = useCollectionQuota(retailCustomerId, true);
    const views = useMemo(
        () => (quota.data ? quotaViewsOf(quota.data, COLLECTION_QUOTA_DAYS) : []),
        [quota.data],
    );
    return (
        <DetailCard title={COPY.quotaTitle} footnote={quota.data ? COPY.quotaLegend : undefined}>
            {quota.isPending ? <Skeleton className="h-16" /> : null}
            {quota.isError ? (
                <p role="alert" className="text-[11px] text-muted-foreground">
                    {COPY.quotaError}
                </p>
            ) : null}
            {quota.data ? (
                <div className="grid grid-cols-3 gap-1.5">
                    {views.map((view) => (
                        <QuotaBox key={view.channel} view={view} />
                    ))}
                </div>
            ) : null}
        </DetailCard>
    );
}
